import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { MagacinLoan, MagacinLoanStatus, MagacinProduct } from '../types';
import { magacinLoansAPI, magacinProductsAPI } from '../services/api';
import './Magacin.css';

const toDate = (d: any) => (d instanceof Date ? d : d ? new Date(d) : new Date());
const fmtDate = (iso?: string) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('sr-RS');
  } catch {
    return iso;
  }
};

const deptOptions = [
  { label: 'Proizvodnja', value: 'Proizvodnja' },
  { label: 'Održavanje', value: 'Održavanje' },
  { label: 'IT', value: 'IT' },
  { label: 'Logistika', value: 'Logistika' },
  { label: 'Magacin', value: 'Magacin' },
  { label: 'Lager', value: 'Lager' },
  { label: 'Ostalo', value: 'Ostalo' }
];

const statusTag = (status: MagacinLoanStatus) => {
  if (status === 'returned') return { label: 'Vraćeno', severity: 'success' as const };
  if (status === 'overdue') return { label: 'Kasni', severity: 'warning' as const };
  return { label: 'Aktivno', severity: 'info' as const };
};

const MagacinLoans: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [loans, setLoans] = useState<MagacinLoan[]>([]);
  const [products, setProducts] = useState<MagacinProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [createVisible, setCreateVisible] = useState(false);
  const [form, setForm] = useState({
    productId: '',
    quantity: 0,
    borrowerName: '',
    borrowerDepartment: 'Proizvodnja',
    loanDate: new Date(),
    expectedReturnDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    notes: ''
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [l, p] = await Promise.all([magacinLoansAPI.getAll(), magacinProductsAPI.getAll()]);
      setLoans(l);
      setProducts(p);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju pozajmica');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const productOptions = useMemo(
    () => products.map((p) => ({ label: p.name, value: p._id })),
    [products]
  );

  const openCreate = () => {
    setForm({
      productId: '',
      quantity: 0,
      borrowerName: '',
      borrowerDepartment: 'Proizvodnja',
      loanDate: new Date(),
      expectedReturnDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      notes: ''
    });
    setCreateVisible(true);
  };

  const canCreate = useMemo(() => {
    return (
      form.productId &&
      form.quantity > 0 &&
      form.borrowerName.trim().length > 0 &&
      form.borrowerDepartment &&
      form.loanDate &&
      form.expectedReturnDate
    );
  }, [form]);

  const create = async () => {
    if (!canCreate) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Popunite obavezna polja', life: 3000 });
      return;
    }
    setLoading(true);
    try {
      const fresh = await magacinProductsAPI.getById(form.productId);
      if (fresh.availableWeight < form.quantity) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Nedovoljno stanje',
          detail: `Dostupno je ${fresh.availableWeight} ${fresh.unit}`,
          life: 4000
        });
        return;
      }

      await magacinLoansAPI.create({
        productId: fresh._id,
        productName: fresh.name,
        productType: fresh.productType,
        unit: fresh.unit,
        quantity: form.quantity,
        borrowerName: form.borrowerName.trim(),
        borrowerDepartment: form.borrowerDepartment,
        loanDate: toDate(form.loanDate),
        expectedReturnDate: toDate(form.expectedReturnDate),
        notes: form.notes || ''
      } as any);

      toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Pozajmica je kreirana', life: 2500 });
      setCreateVisible(false);
      await load();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri kreiranju pozajmice',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const markReturned = (row: MagacinLoan) => {
    confirmDialog({
      header: 'Vraćanje',
      message: `Označiti pozajmicu kao vraćenu za "${row.productName}"?`,
      icon: 'pi pi-question-circle',
      accept: async () => {
        try {
          await magacinLoansAPI.update(row._id, { status: 'returned', actualReturnDate: new Date() } as any);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Pozajmica je vraćena', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri ažuriranju',
            life: 5000
          });
        }
      }
    });
  };

  const del = (row: MagacinLoan) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati pozajmicu za "${row.productName}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await magacinLoansAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Pozajmica obrisana', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju',
            life: 5000
          });
        }
      }
    });
  };

  const statusBody = (row: MagacinLoan) => {
    const s = statusTag(row.status);
    return <Tag value={s.label} severity={s.severity} />;
  };

  const actionsBody = (row: MagacinLoan) => (
    <div className="flex gap-2 justify-content-end">
      {(row.status === 'active' || row.status === 'overdue') && (
        <Button icon="pi pi-check" className="p-button-sm p-button-text" tooltip="Vrati" onClick={() => markReturned(row)} />
      )}
      <Button icon="pi pi-trash" className="p-button-sm p-button-text p-button-danger" tooltip="Obriši" onClick={() => del(row)} />
    </div>
  );

  return (
    <div className="magacin-page p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="magacin-header flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Ko duži</h2>
          <div className="text-600 mt-1">Pozajmice proizvoda</div>
        </div>
        <div className="flex gap-2">
          <Button label="Nova pozajmica" icon="pi pi-plus" severity="info" onClick={openCreate} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={load} />
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <DataTable value={loans} loading={loading} paginator rows={10} emptyMessage="Nema pozajmica" className="p-datatable-sm">
          <Column field="productName" header="Proizvod" sortable />
          <Column field="productType" header="Tip" sortable />
          <Column field="quantity" header="Količina" sortable style={{ width: '130px' }} />
          <Column field="unit" header="Jedinica" sortable style={{ width: '120px' }} />
          <Column field="borrowerName" header="Pozajmio" sortable />
          <Column field="borrowerDepartment" header="Odeljenje" sortable />
          <Column header="Datum pozajmice" body={(r: MagacinLoan) => fmtDate(r.loanDate)} sortable sortField="loanDate" style={{ width: '160px' }} />
          <Column
            header="Očekivani datum vraćanja"
            body={(r: MagacinLoan) => fmtDate(r.expectedReturnDate)}
            sortable
            sortField="expectedReturnDate"
            style={{ width: '200px' }}
          />
          <Column
            header="Stvarni datum vraćanja"
            body={(r: MagacinLoan) => fmtDate(r.actualReturnDate)}
            sortable
            sortField="actualReturnDate"
            style={{ width: '190px' }}
          />
          <Column header="Status" body={statusBody} sortable sortField="status" style={{ width: '140px' }} />
          <Column header="Akcije" body={actionsBody} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header="Nova pozajmica"
        visible={createVisible}
        onHide={() => setCreateVisible(false)}
        style={{ width: '720px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        draggable={false}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setCreateVisible(false)} />
            <Button label="Kreiraj" icon="pi pi-check" severity="info" onClick={create} loading={loading} disabled={!canCreate} />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Proizvod *</label>
            <Dropdown
              value={form.productId}
              options={productOptions}
              onChange={(e) => setForm((p) => ({ ...p, productId: e.value }))}
              appendTo="self"
              filter
              showClear
              placeholder="Izaberite proizvod"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Količina *</label>
            <InputNumber value={form.quantity} onValueChange={(e) => setForm((p) => ({ ...p, quantity: e.value ?? 0 }))} min={0} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Pozajmio *</label>
            <InputText value={form.borrowerName} onChange={(e) => setForm((p) => ({ ...p, borrowerName: e.target.value }))} placeholder="Unesite ime i prezime" />
          </div>
          <div className="field col-12 md:col-6">
            <label>Odeljenje *</label>
            <Dropdown
              value={form.borrowerDepartment}
              options={deptOptions}
              onChange={(e) => setForm((p) => ({ ...p, borrowerDepartment: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Datum pozajmice *</label>
            <Calendar value={form.loanDate} onChange={(e) => setForm((p) => ({ ...p, loanDate: toDate(e.value) }))} dateFormat="dd.mm.yy" showIcon />
          </div>
          <div className="field col-12 md:col-6">
            <label>Očekivani datum vraćanja *</label>
            <Calendar
              value={form.expectedReturnDate}
              onChange={(e) => setForm((p) => ({ ...p, expectedReturnDate: toDate(e.value) }))}
              dateFormat="dd.mm.yy"
              showIcon
            />
          </div>
          <div className="field col-12">
            <label>Napomene</label>
            <InputTextarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={5} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default MagacinLoans;


